import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useLocation } from "react-router-dom";
import {
  FaExpand,
  FaCompress,
  FaThumbsUp,
  FaThumbsDown,
  FaRegThumbsUp,
  FaRegThumbsDown,
  FaRegEdit,
} from "react-icons/fa";
import { db } from "../firebase";
import { getAnalyticsContext, trackAnalyticsEvent } from "../analytics/siteAnalytics";
import { askOpenAI } from "../api/AskOpenAI";
import "./Pluma.css";
import "./PlumaUpgrade.css";
import plumaLogo from "../assets/icons/plumaLogo.png";
import sendIcon from "../assets/icons/sendIcon.png";
import cassettesIcon from "../assets/icons/cassettes.png";
import collimatorsProbesIcon from "../assets/icons/collimatorsProbes.png";
import fiberPatchPanelsIcon from "../assets/icons/Fiber Patch Panels.png";
import fiberCableAssembliesIcon from "../assets/icons/fiberCableAssemblies.png";
import fiberTerminalsClosuresIcon from "../assets/icons/fiberTerminalsClosures.png";
import framesIcon from "../assets/icons/Frames.png";
import lensesIcon from "../assets/icons/Lenses.png";
import passiveOpticalComponentsIcon from "../assets/icons/PassiveOpticalComponents.png";
import photodetectorsIcon from "../assets/icons/photodetectors.png";
import fiberNetworkMonitoringIcon from "../assets/icons/fibernetworkmonitoringicon.png";
import allIcon from "../assets/icons/all.png";
import transceiverIcon from "../assets/icons/transceiver.png";
import aiIcon from "../assets/icons/solutions/ai.webp";
import datacenterIcon from "../assets/icons/solutions/datacenter.png";
import centralOfficeIcon from "../assets/icons/solutions/centralOffice.png";
import outsidePlantIcon from "../assets/icons/solutions/outsidePlant.png";
import fttxIcon from "../assets/icons/solutions/fttx.png";
import ruralBroadbandIcon from "../assets/icons/solutions/gfRural.png";
import wirelessIcon from "../assets/icons/solutions/wireless.png";
import enterpriseIcon from "../assets/icons/solutions/enterprise.png";
import medicalImagingIcon from "../assets/icons/solutions/medicalImaging.png";
import neuroscienceIcon from "../assets/icons/solutions/neuroscience.png";
import specialtyOpticsIcon from "../assets/icons/solutions/specialtyOptics.png";

const PLUMA_PAGE_ICONS = {
  "Fiber Cable Assemblies": fiberCableAssembliesIcon,

  "Fiber Panels": fiberPatchPanelsIcon,

  Cassettes: cassettesIcon,

  "Fiber Terminals & Closures": fiberTerminalsClosuresIcon,

  "Fiber Network Monitoring": fiberNetworkMonitoringIcon,

  Frames: framesIcon,

  "Passive Optical Components": passiveOpticalComponentsIcon,

  Photodetectors: photodetectorsIcon,

  "Collimators & Probes": collimatorsProbesIcon,

  Lenses: lensesIcon,

  Transceivers: transceiverIcon,

  "All Products": allIcon,

  Products: allIcon,
};

const PLUMA_PAGE_PATH_ICONS = {
  "/solutions/ai": aiIcon,

  "/solutions/datacenter": datacenterIcon,

  "/solutions/central-office": centralOfficeIcon,

  "/solutions/outside-plant": outsidePlantIcon,

  "/solutions/fttx": fttxIcon,

  "/solutions/rural-broadband": ruralBroadbandIcon,

  "/solutions/wireless": wirelessIcon,

  "/solutions/enterprise": enterpriseIcon,

  "/solutions/medical-imaging": medicalImagingIcon,

  "/solutions/neuroscience": neuroscienceIcon,

  "/solutions/specialty-optics": specialtyOpticsIcon,
};

const getSavedMessages = () => {
  try {
    const saved = JSON.parse(localStorage.getItem("plumaChatMessages") || "[]");
    return Array.isArray(saved) ? saved : [];
  } catch {
    return [];
  }
};

const getLastPartNumber = (messages = []) => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const value = messages[i]?.partLookup?.partNumber;
    if (value) return value;
  }

  return "";
};

const cleanPlumaText = (value = "") =>
  String(value || "")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/__(.*?)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*#{1,6}\s+/gm, "")
    .replace(/^\s*[-*]\s+/gm, "")
    .trim();

const normalizeUrl = (value = "") => {
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) return value;

  return value.startsWith("/") ? value : `/${value}`;
};

const openInNewTab = (value) => {
  const url = normalizeUrl(value);
  if (!url) return;

  window.open(url, "_blank", "noopener,noreferrer");
};

function ProductCard({ product, pagePath }) {
  if (!product) return null;

  const handleOpen = () => {
    trackAnalyticsEvent("pluma_product_click", {
      productSlug: product.slug || "",
      productName: product.name || "",
      pagePath,
    });

    openInNewTab(product.url);
  };

  return (
    <div className="pluma-rich-card">
      {product.image && (
        <div className="pluma-rich-card-imageWrap">
          <img src={product.image} alt={product.name || "Go!Foton product"} className="pluma-rich-card-image" />
        </div>
      )}

      <div className="pluma-rich-card-body">
        <div className="pluma-rich-card-eyebrow">{product.family || "Go!Foton Product"}</div>

        <strong className="pluma-rich-card-title">{product.name || "Go!Foton Product"}</strong>

        {product.description && <p className="pluma-rich-card-description">{product.description}</p>}

        <div className="pluma-rich-card-actions">
          {product.url && (
            <button type="button" onClick={handleOpen}>
              Product Page
            </button>
          )}

          {product.datasheet && (
            <button
              type="button"
              onClick={() => {
                trackAnalyticsEvent("pluma_datasheet_click", {
                  productSlug: product.slug || "",
                  productName: product.name || "",
                  pagePath,
                });

                openInNewTab(product.datasheet);
              }}
            >
              Datasheet
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function PageCard({ page, pagePath }) {
  if (!page) return null;

  const pageIcon = PLUMA_PAGE_PATH_ICONS[page.path] || PLUMA_PAGE_ICONS[page.title];

  return (
    <div className="pluma-rich-card pluma-page-card">
      <div className="pluma-page-icon">
        {pageIcon ? (
          <span
            className="pluma-page-icon-mask"
            style={{
              WebkitMaskImage: `url(${pageIcon})`,
              maskImage: `url(${pageIcon})`,
            }}
          />
        ) : page.type === "Careers" ? (
          "◎"
        ) : page.type === "Solution" ? (
          "◇"
        ) : (
          "↗"
        )}
      </div>

      <div className="pluma-rich-card-body">
        <div className="pluma-rich-card-eyebrow">{page.type || "Go!Foton Page"}</div>

        <strong className="pluma-rich-card-title">{page.title}</strong>

        {page.description && <p className="pluma-rich-card-description">{page.description}</p>}

        <div className="pluma-rich-card-actions">
          <button
            type="button"
            onClick={() => {
              trackAnalyticsEvent("pluma_page_click", {
                pageName: page.title || "",
                destinationPath: page.path || "",
                pagePath,
              });

              openInNewTab(page.path);
            }}
          >
            Open Page
          </button>
        </div>
      </div>
    </div>
  );
}

function MasterPartCard({ part }) {
  if (!part) return null;

  return (
    <div className="pluma-master-card">
      <div className="pluma-rich-card-eyebrow">{part.type || "Master Parts List"}</div>

      <strong>{part.partNumber || "Part Number"}</strong>

      {part.description && <p>{part.description}</p>}
    </div>
  );
}

function PartLookupCard({ lookup, pagePath }) {
  const [showAllDetails, setShowAllDetails] = useState(false);

  if (!lookup) return null;

  const details = Array.isArray(lookup.details) ? lookup.details : [];

  const visibleDetails = showAllDetails ? details : details.slice(0, 3);

  return (
    <div className={`pluma-part-card ${lookup.valid === false ? "is-invalid" : ""}`}>
      <div className="pluma-part-card-top">
        <div>
          <div className="pluma-rich-card-eyebrow">Part Number Lookup</div>

          <strong className="pluma-part-number">{lookup.partNumber || "Unknown"}</strong>
        </div>

        <span
          className={`pluma-part-status ${
            lookup.complete ? "valid" : lookup.status === "invalid" ? "invalid" : "partial"
          }`}
        >
          {lookup.complete
            ? "Matched"
            : lookup.status === "ambiguous"
              ? "Multiple Matches"
              : lookup.status === "invalid"
                ? "Not Matched"
                : "Partial"}
        </span>
      </div>

      {lookup.productName && <div className="pluma-part-product">{lookup.productName}</div>}

      {lookup.description && <p className="pluma-part-description">{lookup.description}</p>}

      {details.length > 0 && (
        <div className="pluma-part-details">
          {visibleDetails.map((detail, index) => (
            <div key={`${detail.label}-${detail.code}-${index}`}>
              <span>{detail.label}</span>
              <strong>{detail.description || detail.code}</strong>
            </div>
          ))}
        </div>
      )}

      {details.length > 3 && (
        <button type="button" className="pluma-part-expand" onClick={() => setShowAllDetails((prev) => !prev)}>
          {showAllDetails ? "Show less" : `Show ${details.length - 3} more details`}
        </button>
      )}

      {Array.isArray(lookup.requiredMissing) && lookup.requiredMissing.length > 0 && (
        <div className="pluma-part-warning">Still needed: {lookup.requiredMissing.join(", ")}</div>
      )}

      <div className="pluma-rich-card-actions">
        {lookup.configureUrl && (
          <button
            type="button"
            onClick={() => {
              trackAnalyticsEvent("pluma_part_configure_click", {
                partNumber: lookup.partNumber || "",
                productName: lookup.productName || "",
                pagePath,
              });

              openInNewTab(lookup.configureUrl);
            }}
          >
            Configure
          </button>
        )}

        {lookup.productPage && (
          <button
            type="button"
            onClick={() => {
              trackAnalyticsEvent("pluma_product_click", {
                productSlug: lookup.productSlug || "",
                productName: lookup.productName || "",
                pagePath,
              });

              openInNewTab(lookup.productPage);
            }}
          >
            Product Page
          </button>
        )}

        {lookup.datasheet && (
          <button
            type="button"
            onClick={() => {
              trackAnalyticsEvent("pluma_datasheet_click", {
                partNumber: lookup.partNumber || "",
                productName: lookup.productName || "",
                pagePath,
              });

              openInNewTab(lookup.datasheet);
            }}
          >
            Datasheet
          </button>
        )}
      </div>
    </div>
  );
}

function ProductComparisonCard({ comparison, pagePath }) {
  if (!comparison?.found || comparison.products?.length < 2) {
    return null;
  }

  return (
    <div className="pluma-comparison">
      <div className="pluma-rich-card-eyebrow">Product Comparison</div>

      <div className="pluma-comparison-scroll">
        <table>
          <thead>
            <tr>
              <th>Specification</th>

              {comparison.products.map((product, index) => (
                <th key={`${product.slug}-${index}`}>{product.name}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {comparison.rows.map((row, rowIndex) => (
              <tr key={`${row.label}-${rowIndex}`}>
                <td>{row.label}</td>

                {comparison.products.map((product, productIndex) => (
                  <td key={`${product.slug}-${productIndex}`}>{row.values[productIndex] || "Not stated"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pluma-rich-card-actions">
        {comparison.products.map((product, index) =>
          product.url ? (
            <button
              key={`${product.slug}-${index}`}
              type="button"
              onClick={() => {
                trackAnalyticsEvent("pluma_comparison_product_click", {
                  productSlug: product.slug || "",

                  productName: product.name || "",

                  pagePath,
                });

                openInNewTab(product.url);
              }}
            >
              {product.name}
            </button>
          ) : null
        )}
      </div>
    </div>
  );
}

function AnswerEvidence({ sources = [], confidence }) {
  const uniqueSources = [
    ...new Map(
      (Array.isArray(sources) ? sources : []).filter((source) => source?.label).map((source) => [source.label, source])
    ).values(),
  ].slice(0, 5);

  if (!uniqueSources.length && !confidence?.level) {
    return null;
  }

  return (
    <div className="pluma-evidence">
      {uniqueSources.length > 0 && (
        <div className="pluma-source-row">
          <span className="pluma-evidence-label">Sources</span>

          {uniqueSources.map((source, index) => (
            <span key={`${source.label}-${index}`} className="pluma-source-chip" title={source.detail || source.label}>
              {source.label}
            </span>
          ))}
        </div>
      )}

      {confidence?.level && (
        <div className={`pluma-confidence pluma-confidence-${confidence.level}`} title={confidence.reason || ""}>
          <span />

          {confidence.label}
        </div>
      )}
    </div>
  );
}

function ActionCards({ actions = [], pagePath }) {
  if (!actions.length) return null;

  return (
    <div className="pluma-action-list">
      {actions
        .filter((action) => action?.url)
        .map((action, index) => (
          <button
            key={`${action.url}-${index}`}
            type="button"
            className="pluma-action-card"
            onClick={() => {
              trackAnalyticsEvent("pluma_action_click", {
                actionType: action.type || "",
                actionLabel: action.label || "",
                destinationUrl: action.url || "",
                pagePath,
              });

              openInNewTab(action.url);
            }}
          >
            <span>{action.label || "Open"}</span>
            <strong>↗</strong>
          </button>
        ))}
    </div>
  );
}

function Pluma() {
  const location = useLocation();

  const [showChat, setShowChat] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [inputMessage, setInputMessage] = useState("");
  const [chatMessages, setChatMessages] = useState(getSavedMessages);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("Thinking...");
  const [showTeaser, setShowTeaser] = useState(false);
  const [teaserIndex, setTeaserIndex] = useState(0);
  const [footerSafeBottom, setFooterSafeBottom] = useState(0);

  const latestAssistantMessageRef = useRef(null);
  const messagesRef = useRef(null);

  const pendingAssistantScrollRef = useRef(false);
  const autoOpenedUrlsRef = useRef(new Set());

  const teaserMessages = useMemo(
    () => [
      "Tell us about your project",
      "Need help finding a product?",
      "Ask me about a part number",
      "Ask me to find a datasheet",
    ],
    []
  );

  useEffect(() => {
    localStorage.setItem("plumaChatMessages", JSON.stringify(chatMessages.slice(-30)));
  }, [chatMessages]);

  useEffect(() => {
    /*
    Wait until Pluma has completely finished.
    We specifically want to position the final
    assistant response, not the loading bubble
    or the user's question.
  */
    if (!pendingAssistantScrollRef.current || isLoading) {
      return undefined;
    }

    let scrollFrame = null;

    const layoutFrame = requestAnimationFrame(() => {
      const container = messagesRef.current;

      const response = latestAssistantMessageRef.current;

      if (!container || !response) {
        return;
      }

      /*
        Remove any extra bottom padding left
        from the previous response first.

        This lets us calculate the amount of
        space required for the current response.
      */
      container.style.paddingBottom = "";

      const computedStyle = window.getComputedStyle(container);

      const basePaddingBottom = Number.parseFloat(computedStyle.paddingBottom) || 0;

      const containerRect = container.getBoundingClientRect();

      const responseRect = response.getBoundingClientRect();

      /*
        Leave comfortable space between the
        top of the response and the top edge
        of the message viewport.
      */
      const topSpacing = 18;

      const targetTop = Math.max(0, container.scrollTop + (responseRect.top - containerRect.top) - topSpacing);

      /*
        A short answer normally cannot be
        scrolled all the way to the top because
        there is not enough content underneath it.

        Add only as much invisible bottom space
        as this particular viewport needs.
      */
      const currentMaxScrollTop = Math.max(0, container.scrollHeight - container.clientHeight);

      const extraBottomSpace = Math.max(0, targetTop - currentMaxScrollTop + 12);

      if (extraBottomSpace > 0) {
        container.style.paddingBottom = `${basePaddingBottom + extraBottomSpace}px`;
      }

      /*
        Wait one more frame so the browser
        accounts for the additional scroll room,
        then smoothly position the beginning
        of Pluma's response.
      */
      scrollFrame = requestAnimationFrame(() => {
        container.scrollTo({
          top: targetTop,
          behavior: "smooth",
        });

        pendingAssistantScrollRef.current = false;
      });
    });

    return () => {
      cancelAnimationFrame(layoutFrame);

      if (scrollFrame) {
        cancelAnimationFrame(scrollFrame);
      }
    };
  }, [chatMessages, isLoading]);

  useEffect(() => {
    if (!showChat) return undefined;

    const frame = requestAnimationFrame(() => {
      const container = messagesRef.current;

      if (!container) return;

      container.scrollTop = container.scrollHeight;
    });

    return () => cancelAnimationFrame(frame);
  }, [showChat, expanded]);

  useEffect(() => {
    if (showChat) {
      setShowTeaser(false);
      return undefined;
    }

    const timer = setTimeout(() => {
      setShowTeaser(true);
    }, 10000);

    return () => clearTimeout(timer);
  }, [showChat]);

  useEffect(() => {
    if (!showTeaser || showChat) return undefined;

    const timer = setInterval(() => {
      setTeaserIndex((prev) => (prev + 1) % teaserMessages.length);
    }, 15000);

    return () => clearInterval(timer);
  }, [showTeaser, showChat, teaserMessages]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) {
      setShowChat(false);
      setShowTeaser(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    let animationFrame = null;

    const updateFooterPosition = () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      animationFrame = requestAnimationFrame(() => {
        /*
        Only react to the dark legal/footer-bottom
        section, not the entire large footer.
      */
        const footerBottom = document.querySelector(".footer-bottom");

        if (!footerBottom) {
          setFooterSafeBottom(0);
          return;
        }

        const rect = footerBottom.getBoundingClientRect();

        const footerIsVisible = rect.top < window.innerHeight && rect.bottom > 0;

        if (!footerIsVisible) {
          setFooterSafeBottom(0);
          return;
        }

        /*
        Amount of footer currently occupying the
        bottom of the viewport.

        Add 18px so Pluma sits slightly above it
        rather than touching the footer.
      */
        const safeBottom = Math.max(0, window.innerHeight - rect.top + 18);

        setFooterSafeBottom(safeBottom);
      });
    };

    updateFooterPosition();

    window.addEventListener("scroll", updateFooterPosition, {
      passive: true,
    });

    window.addEventListener("resize", updateFooterPosition);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }

      window.removeEventListener("scroll", updateFooterPosition);

      window.removeEventListener("resize", updateFooterPosition);
    };
  }, []);

  const startNewConversation = () => {
    setChatMessages([]);
    setInputMessage("");
    setLoadingText("Thinking...");
    autoOpenedUrlsRef.current.clear();

    localStorage.removeItem("plumaChatMessages");

    trackAnalyticsEvent("pluma_new_conversation", {
      pagePath: location.pathname,
    });
  };

  const openPluma = () => {
    setShowChat(true);
    setShowTeaser(false);

    trackAnalyticsEvent("pluma_open", {
      pagePath: location.pathname,
    });
  };

  const submitFeedback = async (chatDocId, value) => {
    if (!chatDocId) return;

    try {
      await updateDoc(doc(db, "aiChats", chatDocId), {
        feedback: value,
        feedbackAt: serverTimestamp(),
        feedbackPage: location.pathname,
      });

      await trackAnalyticsEvent("pluma_feedback", {
        chatDocId,
        feedback: value,
        pagePath: location.pathname,
      });

      setChatMessages((prev) =>
        prev.map((message) => (message.chatDocId === chatDocId ? { ...message, feedback: value } : message))
      );
    } catch (error) {
      console.error("Pluma feedback error:", error);
    }
  };

  const runAutoActions = (actions = []) => {
    actions.forEach((action) => {
      if (!action?.autoOpen || !action?.url) return;
      if (autoOpenedUrlsRef.current.has(action.url)) return;

      autoOpenedUrlsRef.current.add(action.url);

      trackAnalyticsEvent("pluma_auto_open", {
        actionType: action.type || "",
        actionLabel: action.label || "",
        destinationUrl: action.url,
        pagePath: location.pathname,
      });

      openInNewTab(action.url);
    });
  };

  const handleSend = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();

    /*
  Remove the extra scroll space from the
  previous answer before beginning a new turn.
*/
    if (messagesRef.current) {
      messagesRef.current.style.paddingBottom = "";
    }

    pendingAssistantScrollRef.current = true;

    const previousMessages = chatMessages.slice(-12);
    const lastPartNumber = getLastPartNumber(previousMessages);

    setInputMessage("");

    setChatMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
      },
    ]);

    setIsLoading(true);
    setLoadingText("Understanding your question...");

    try {
      const analyticsContext = await getAnalyticsContext();

      const response = await askOpenAI({
        messages: [
          ...previousMessages.map((message) => ({
            role: message.role === "assistant" ? "assistant" : "user",
            content: message.content || "",
          })),
          {
            role: "user",
            content: userMessage,
          },
        ],

        pagePath: `${location.pathname}${location.search}`,
        visitorId: analyticsContext?.visitorId || "",
        sessionId: analyticsContext?.sessionId || "",
        lastPartNumber,
        onStatus: ({ status }) => {
          if (status) {
            setLoadingText(status);
          }
        },
      });

      const matchedProducts = Array.isArray(response.products)
        ? response.products.map((product) => product.name).filter(Boolean)
        : [];

      const recommendedPages = Array.isArray(response.pages)
        ? response.pages.map((page) => ({
            title: page.title || "",
            path: page.path || "",
          }))
        : [];

      const chatRef = await addDoc(collection(db, "aiChats"), {
        page: `${location.pathname}${location.search}`,
        visitorId: analyticsContext?.visitorId || "",
        sessionId: analyticsContext?.sessionId || "",
        userMessage,
        assistantReply: response.reply || "",
        category: response.category || "General",
        model: response.model || "",
        tokenUsage: {
          inputTokens: Number(response.tokenUsage?.inputTokens || 0),

          outputTokens: Number(response.tokenUsage?.outputTokens || 0),

          totalTokens: Number(response.tokenUsage?.totalTokens || 0),

          cachedInputTokens: Number(response.tokenUsage?.cachedInputTokens || 0),

          reasoningTokens: Number(response.tokenUsage?.reasoningTokens || 0),

          apiCalls: Number(response.tokenUsage?.apiCalls || 0),
        },
        toolCalls: Array.isArray(response.toolCalls) ? response.toolCalls : [],
        matchedProducts,
        recommendedPages,
        partLookup: response.partLookup || null,
        masterPartMatches: Array.isArray(response.parts) ? response.parts.length : 0,
        createdAt: serverTimestamp(),
        feedback: null,
        feedbackAt: null,
        feedbackPage: location.pathname,
        sources: Array.isArray(response.sources) ? response.sources : [],

        confidence: response.confidence || null,

        comparison: response.comparison || null,
      });

      await trackAnalyticsEvent("pluma_message", {
        chatDocId: chatRef.id,
        userMessage,
        assistantReply: response.reply || "",
        category: response.category || "General",
        model: response.model || "",
        inputTokens: Number(response.tokenUsage?.inputTokens || 0),

        outputTokens: Number(response.tokenUsage?.outputTokens || 0),

        totalTokens: Number(response.tokenUsage?.totalTokens || 0),

        cachedInputTokens: Number(response.tokenUsage?.cachedInputTokens || 0),

        reasoningTokens: Number(response.tokenUsage?.reasoningTokens || 0),

        openAIApiCalls: Number(response.tokenUsage?.apiCalls || 0),
        toolCalls: Array.isArray(response.toolCalls) ? response.toolCalls : [],
        matchedProducts,
        recommendedPages: recommendedPages.map((page) => page.path).filter(Boolean),
        partNumber: response.partLookup?.partNumber || "",
        pagePath: location.pathname,
        confidenceLevel: response.confidence?.level || "",

        sourceLabels: (response.sources || []).map((source) => source.label).filter(Boolean),
      });

      for (const toolName of response.toolCalls || []) {
        await trackAnalyticsEvent("pluma_tool_call", {
          chatDocId: chatRef.id,
          toolName,
          category: response.category || "General",
          pagePath: location.pathname,
        });
      }

      const assistantMessage = {
        role: "assistant",
        content: response.reply || "",
        category: response.category || "General",
        products: Array.isArray(response.products) ? response.products : [],
        pages: Array.isArray(response.pages) ? response.pages : [],
        parts: Array.isArray(response.parts) ? response.parts : [],
        actions: Array.isArray(response.actions) ? response.actions : [],
        partLookup: response.partLookup || null,
        toolCalls: Array.isArray(response.toolCalls) ? response.toolCalls : [],
        chatDocId: chatRef.id,
        feedback: null,
        sources: Array.isArray(response.sources) ? response.sources : [],

        confidence: response.confidence || null,

        comparison: response.comparison || null,
      };

      setChatMessages((prev) => [...prev, assistantMessage]);

      runAutoActions(assistantMessage.actions);
    } catch (error) {
      console.error("Pluma error:", error);

      setChatMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I could not complete that request right now. Please try again, or contact Go!Foton at +1.732.469.9650.",
        },
      ]);
    } finally {
      setIsLoading(false);
      setLoadingText("Thinking...");
    }
  };

  if (location.pathname.startsWith("/admin")) {
    return null;
  }

  return (
    <>
      {!showChat && showTeaser && (
        <button
          className="pluma-teaser fade-in-slide-left"
          onClick={openPluma}
          aria-label="Open Pluma AI teaser"
          style={
            footerSafeBottom > 0
              ? {
                  bottom: `${footerSafeBottom}px`,
                }
              : undefined
          }
        >
          {" "}
          <span key={teaserIndex} className="pluma-teaser-text teaser-text-fade">
            {teaserMessages[teaserIndex]}
          </span>
        </button>
      )}

      {!showChat && (
        <button
          className="pluma-launcher"
          onClick={openPluma}
          aria-label="Open Pluma AI"
          style={
            footerSafeBottom > 0
              ? {
                  bottom: `${footerSafeBottom}px`,
                }
              : undefined
          }
        >
          {" "}
          <img src={plumaLogo} alt="Pluma AI" />
        </button>
      )}

      {showChat && (
        <>
          {expanded && <div className="pluma-expanded-backdrop" onClick={() => setExpanded(false)} />}

          <div
            className={`pluma-widget ${expanded ? "pluma-widget-expanded" : ""}`}
            style={
              !expanded && footerSafeBottom > 0
                ? {
                    bottom: `${footerSafeBottom}px`,
                  }
                : undefined
            }
          >
            {" "}
            <div className="pluma-header">
              <div className="pluma-header-brand">
                <img src={plumaLogo} alt="" />
                <div>
                  <strong>Pluma</strong>
                  <span>Go!Foton AI</span>
                </div>
              </div>

              <div className="pluma-header-actions">
                <button
                  type="button"
                  className="pluma-header-btn"
                  onClick={startNewConversation}
                  aria-label="New conversation"
                  title="New conversation"
                >
                  <FaRegEdit size={15} />
                </button>
                <button
                  type="button"
                  className="pluma-header-btn"
                  onClick={() => setExpanded((prev) => !prev)}
                  aria-label={expanded ? "Shrink Pluma" : "Expand Pluma"}
                  title={expanded ? "Shrink" : "Expand"}
                >
                  {expanded ? <FaCompress size={15} /> : <FaExpand size={15} />}
                </button>

                <button
                  type="button"
                  className="pluma-close"
                  onClick={() => setShowChat(false)}
                  aria-label="Close Pluma"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="pluma-messages" ref={messagesRef}>
              {chatMessages.length === 0 && (
                <div className="pluma-welcome">
                  <img src={plumaLogo} alt="" />
                  <h3>How can I help?</h3>
                  <p>Ask me to find products, decode a part number, read a datasheet, or take you to the right page.</p>

                  <div className="pluma-starter-grid">
                    <button type="button" onClick={() => setInputMessage("Help me find a fiber product")}>
                      Find a product
                    </button>
                    <button type="button" onClick={() => setInputMessage("I have a part number I want to look up")}>
                      Part number lookup
                    </button>
                    <button type="button" onClick={() => setInputMessage("Show me your AI data center solutions")}>
                      AI solutions
                    </button>
                    <button type="button" onClick={() => setInputMessage("Take me to careers")}>
                      Careers
                    </button>
                  </div>
                </div>
              )}

              {chatMessages.map((message, index) => (
                <div
                  key={`${message.role}-${index}`}

                  ref={
                    message.role === "assistant" && index === chatMessages.length - 1 ? latestAssistantMessageRef : null
                  }
                  className={message.role === "user" ? "pluma-user-msg" : "pluma-ai-msg fade-in-up"}
                >
                  <p className="pluma-message-text">{cleanPlumaText(message.content)}</p>

                  {message.role === "assistant" && (
                    <AnswerEvidence sources={message.sources} confidence={message.confidence} />
                  )}

                  {message.comparison && (
                    <ProductComparisonCard comparison={message.comparison} pagePath={location.pathname} />
                  )}

                  {message.role === "assistant" && (
                    <>
                      {message.partLookup && (
                        <PartLookupCard lookup={message.partLookup} pagePath={location.pathname} />
                      )}

                      {message.products?.length > 0 && (
                        <div className="pluma-card-stack">
                          {message.products.map((product, productIndex) => (
                            <ProductCard
                              key={`${product.slug || product.id || "product"}-${productIndex}`}
                              product={product}
                              pagePath={location.pathname}
                            />
                          ))}
                        </div>
                      )}

                      {message.pages?.length > 0 && (
                        <div className="pluma-card-stack">
                          {message.pages.map((page, pageIndex) => (
                            <PageCard
                              key={`${page.path || "page"}-${pageIndex}`}
                              page={page}
                              pagePath={location.pathname}
                            />
                          ))}
                        </div>
                      )}

                      {message.parts?.length > 0 && (
                        <div className="pluma-card-stack">
                          {message.parts.map((part, partIndex) => (
                            <MasterPartCard key={`${part.partNumber || "part"}-${partIndex}`} part={part} />
                          ))}
                        </div>
                      )}

                      <ActionCards actions={message.actions || []} pagePath={location.pathname} />

                      {message.category && <div className="pluma-category-badge">{message.category}</div>}

                      <div className="pluma-feedback">
                        <button
                          type="button"
                          className={`pluma-feedback-btn ${message.feedback === "up" ? "active" : ""}`}
                          onClick={() => submitFeedback(message.chatDocId, "up")}
                          aria-label="Thumbs up"
                          title="Helpful"
                        >
                          {message.feedback === "up" ? <FaThumbsUp size={14} /> : <FaRegThumbsUp size={14} />}
                        </button>

                        <button
                          type="button"
                          className={`pluma-feedback-btn ${message.feedback === "down" ? "active" : ""}`}
                          onClick={() => submitFeedback(message.chatDocId, "down")}
                          aria-label="Thumbs down"
                          title="Not helpful"
                        >
                          {message.feedback === "down" ? <FaThumbsDown size={14} /> : <FaRegThumbsDown size={14} />}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="pluma-ai-msg fade-in-up">
                  <div className="pluma-tool-loading">
                    <div className="pluma-orbit-loader">
                      <span />
                      <span />
                      <span />
                    </div>

                    <div>
                      <strong>{loadingText}</strong>
                      <small>Pluma is working with Go!Foton data.</small>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="pluma-disclaimer">
              Results are AI-assisted and benefit from human review. <a href="tel:+17324699650">Call us</a> for more
              information.
            </div>
            <form
              className="pluma-input"
              onSubmit={(event) => {
                event.preventDefault();
                handleSend();
              }}
            >
              <input
                value={inputMessage}
                onChange={(event) => setInputMessage(event.target.value)}
                placeholder="Ask Pluma anything..."
                className="pluma-text-input"
                disabled={isLoading}
              />

              <button
                type="submit"
                disabled={isLoading || !inputMessage.trim()}
                className="pluma-send-btn"
                aria-label="Send"
              >
                <img src={sendIcon} alt="Send" />
              </button>
            </form>
          </div>
        </>
      )}
    </>
  );
}

export default Pluma;
